import { useEffect, useState, type ReactElement, type ReactNode } from 'react'
import { ArrowLeft, RotateCcw } from 'lucide-react'
import { Button } from './ui/button.js'
import { Progress } from './ui/progress.js'
import { useDemoStore } from '../state/store.js'
import { cn } from '@/lib/utils.js'

const RESTART_STEP = 'start'
const STEP_CHANGED_EVENT = 'veritact:step-changed'

interface AppShellProps {
  title?: string | undefined
  showBack?: boolean
  onBack?: (() => void) | undefined
  /** 0..1, omit for screens without a progress bar */
  progress?: number | undefined
  /** Optional right-side header slot (help, cancel, etc.) */
  rightAction?: ReactNode
  footer?: ReactNode
  /**
   * When true, hide header + footer chrome and remove the main padding so
   * the child renders edge-to-edge inside the phone-frame on desktop / the
   * full viewport on mobile. The child is responsible for its own scrolling.
   */
  fullbleed?: boolean
  children: ReactNode
}

/**
 * `true` once the viewport is desktop-wide (≥ 768px). Re-evaluates on resize.
 * Initial state is computed SYNCHRONOUSLY from the current viewport via the
 * useState initializer — otherwise the mobile path renders first and then
 * the tree reconciles to desktop on the next tick, which both flashes the
 * layout on every screen change and (more critically) unmount/remounts any
 * <video> element inside the phone frame mid-load, killing the playback.
 */
const useIsDesktop = (): boolean => {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' &&
    window.matchMedia?.('(min-width: 768px)').matches === true,
  )
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mql = window.matchMedia('(min-width: 768px)')
    const update = (): void => setIsDesktop(mql.matches)
    update()
    mql.addEventListener('change', update)
    return () => mql.removeEventListener('change', update)
  }, [])
  return isDesktop
}

/**
 * The single column app shell. On phones the column fills the viewport. On
 * tablets/desktop it renders inside a centered, polished phone frame with
 * Veritact marketing chrome around it — this turns the demo into a pitch
 * device on a big screen without changing the layout customers see.
 */
export const AppShell = ({
  title,
  showBack,
  onBack,
  progress,
  rightAction,
  footer,
  fullbleed,
  children,
}: AppShellProps): ReactElement => {
  const isDesktop = useIsDesktop()
  const { dispatch } = useDemoStore()

  const restartDemo = (): void => {
    dispatch({ type: 'RESET' })
    const params = new URLSearchParams(window.location.search)
    params.set('step', RESTART_STEP)
    window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`)
    window.dispatchEvent(new Event(STEP_CHANGED_EVENT))
  }

  const column = (
    <div className="flex flex-col h-full min-h-[100dvh] md:min-h-0 bg-background text-foreground">
      {!fullbleed && (
        <header className="grid grid-cols-[2.5rem_1fr_2.5rem] items-center px-4 min-h-14 border-b border-border shrink-0 pt-[env(safe-area-inset-top)]">
          <div className="flex items-center justify-start">
            {showBack && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onBack}
                aria-label="Back"
                className="h-9 w-9 -ml-1"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
          </div>
          <div className="flex items-center justify-center min-w-0">
            {title && <h2 className="text-base font-semibold truncate">{title}</h2>}
          </div>
          <div className="flex items-center justify-end">{rightAction}</div>
        </header>
      )}
      {!fullbleed && progress != null && (
        <Progress
          value={Math.max(0, Math.min(1, progress)) * 100}
          className="h-1 rounded-none bg-muted"
        />
      )}
      <main
        className={cn(
          'flex-1 overflow-y-auto',
          fullbleed ? 'relative p-0' : 'px-5 py-5',
        )}
      >
        {children}
      </main>
      {!fullbleed && footer && (
        <footer className="px-5 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-border bg-background shrink-0">
          {footer}
        </footer>
      )}
    </div>
  )

  if (!isDesktop) {
    return <div className="mx-auto max-w-md">{column}</div>
  }

  return (
    <div className="relative flex min-h-[100dvh] bg-gradient-to-br from-secondary via-background to-secondary items-center justify-center px-8 py-10">
      <Button
        variant="ghost"
        size="sm"
        onClick={restartDemo}
        className="absolute top-6 right-6 text-xs text-muted-foreground hover:text-foreground"
      >
        <RotateCcw className="h-4 w-4 mr-2" />
        Restart demo
      </Button>
      <PhoneFrame>{column}</PhoneFrame>
    </div>
  )
}

/**
 * Stylized phone frame — fixed iPhone-ish aspect ratio with subtle bezel and
 * a hairline notch indicator. The inner viewport gets the actual column.
 */
const PhoneFrame = ({ children }: { children: ReactNode }): ReactElement => (
  <div className="relative shrink-0">
    {/* Outer bezel. Width is fixed at 350px — slightly narrower than a real
        iPhone but the typography scale inside is already tuned for desktop
        preview, and the narrower bezel sits more comfortably next to the
        marketing column. Only the height shrinks to fit short viewports;
        the inner viewport handles its own overflow-y scrolling. */}
    <div
      className="relative rounded-[2.75rem] bg-foreground/90 p-2 shadow-2xl ring-1 ring-foreground/10"
      style={{ height: 'min(852px, 92vh)', width: '350px' }}
    >
      {/* Inner viewport. */}
      <div className="desktop-phone-typography relative h-full w-full overflow-hidden rounded-[2.25rem] bg-background">
        {/* Dynamic-island stand-in. */}
        <div className="pointer-events-none absolute left-1/2 top-2 z-30 h-6 w-28 -translate-x-1/2 rounded-full bg-foreground/90" />
        <div className="absolute inset-0 overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
    {/* Side buttons (decorative). */}
    <div className="absolute -left-1 top-32 h-16 w-1 rounded-full bg-foreground/80" />
    <div className="absolute -left-1 top-52 h-24 w-1 rounded-full bg-foreground/80" />
    <div className="absolute -right-1 top-40 h-20 w-1 rounded-full bg-foreground/80" />
  </div>
)
